import React, { useEffect, useState } from 'react';
import CrudTable from '../../components/common/CrudTable';
import { lessonAPI, groupAPI } from '../../api/services';
import { FiUsers, FiX, FiCheckCircle } from 'react-icons/fi';

const LessonsAdmin = () => {
  const [groups, setGroups] = useState([]);
  
  // Attendance Modal State
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [attendanceSheet, setAttendanceSheet] = useState([]);
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [savingSheet, setSavingSheet] = useState(false);

  useEffect(() => {
    groupAPI.getAll().then(res => setGroups(res.data?.data || [])).catch(() => {});
  }, []);

  const openAttendanceModal = async (lesson) => {
    setSelectedLesson(lesson);
    setShowAttendanceModal(true);
    setLoadingSheet(true);
    try {
      const res = await lessonAPI.getAttendanceSheet(lesson.id);
      setAttendanceSheet(res.data?.data || []);
    } catch (err) {
      alert("Failed to load attendance sheet");
    } finally {
      setLoadingSheet(false);
    }
  };

  const handleStatusChange = (enrollmentId, status) => {
    setAttendanceSheet(prev => prev.map(item => 
      item.enrollmentId === enrollmentId ? { ...item, status } : item
    ));
  };

  const handleNoteChange = (enrollmentId, note) => {
    setAttendanceSheet(prev => prev.map(item => 
      item.enrollmentId === enrollmentId ? { ...item, note } : item
    ));
  };

  const saveAttendanceSheet = async () => {
    setSavingSheet(true);
    try {
      await lessonAPI.saveBulkAttendance(selectedLesson.id, attendanceSheet);
      setShowAttendanceModal(false);
      setSelectedLesson(null);
    } catch (err) {
      alert("Failed to save attendance");
    } finally {
      setSavingSheet(false);
    }
  };

  const COLUMNS = [
    { key: 'lessonDate', label: 'Date' },
    { key: 'startTime', label: 'Time' },
    { key: 'groupName', label: 'Group' },
  ];

  const FORM_FIELDS = [
    { 
      name: 'groupId', 
      label: 'Group', 
      type: 'select', 
      options: groups.map(g => ({ label: g.name, value: g.id })) 
    },
    { name: 'lessonDate', label: 'Date', type: 'date' },
    { name: 'startTime', label: 'Start Time', type: 'time' },
  ];

  const renderAttendanceModal = () => {
    if (!showAttendanceModal) return null;

    return (
      <div style={overlayStyle} onClick={() => setShowAttendanceModal(false)}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: 0 }}>Take Attendance</h2>
              <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                {selectedLesson?.groupName} - {selectedLesson?.lessonDate}
              </p>
            </div>
            <button onClick={() => setShowAttendanceModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}>
              <FiX />
            </button>
          </div>

          {loadingSheet ? (
            <div style={{ textAlign: 'center', padding: 40 }}>Loading roster...</div>
          ) : attendanceSheet.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
              No students enrolled in this group.
            </div>
          ) : (
            <div style={{ maxHeight: '60vh', overflowY: 'auto', marginBottom: 20 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid var(--border-color)' }}>Student</th>
                    <th style={{ textAlign: 'center', padding: '12px 8px', borderBottom: '1px solid var(--border-color)' }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '1px solid var(--border-color)' }}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceSheet.map(student => (
                    <tr key={student.enrollmentId}>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid var(--border-color)' }}>
                        {student.studentName}
                      </td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid var(--border-color)', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                          <StatusBtn 
                            label="P" title="Present"
                            active={student.status === 'PRESENT'} 
                            color="#10b981" 
                            onClick={() => handleStatusChange(student.enrollmentId, 'PRESENT')} 
                          />
                          <StatusBtn 
                            label="A" title="Absent"
                            active={student.status === 'ABSENT'} 
                            color="#ef4444" 
                            onClick={() => handleStatusChange(student.enrollmentId, 'ABSENT')} 
                          />
                          <StatusBtn 
                            label="L" title="Late"
                            active={student.status === 'LATE'} 
                            color="#f59e0b" 
                            onClick={() => handleStatusChange(student.enrollmentId, 'LATE')} 
                          />
                          <StatusBtn 
                            label="E" title="Excused"
                            active={student.status === 'EXCUSED'} 
                            color="#3b82f6" 
                            onClick={() => handleStatusChange(student.enrollmentId, 'EXCUSED')} 
                          />
                        </div>
                      </td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid var(--border-color)' }}>
                        <input 
                          type="text" 
                          placeholder="Note..." 
                          value={student.note || ''}
                          onChange={(e) => handleNoteChange(student.enrollmentId, e.target.value)}
                          style={{ width: '100%', padding: '6px 8px', border: '1px solid var(--border-color)', borderRadius: 6 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button className="btn btn-secondary" onClick={() => setShowAttendanceModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveAttendanceSheet} disabled={savingSheet || loadingSheet}>
              {savingSheet ? 'Saving...' : 'Save Attendance'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <CrudTable
        title="Lessons"
        entityName="Lesson"
        columns={COLUMNS}
        formFields={FORM_FIELDS}
        fetchData={async () => {
          const res = await lessonAPI.getAll();
          return res.data?.data || [];
        }}
        onAdd={(data) => lessonAPI.add(data)}
        onUpdate={(id, data) => lessonAPI.update(id, data)}
        onDelete={(id) => lessonAPI.delete(id)}
        customActions={(item) => (
          <button 
            className="btn btn-secondary btn-sm" 
            title="Take Attendance"
            onClick={() => openAttendanceModal(item)}
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', padding: '4px 8px' }}
          >
            <FiCheckCircle />
            <span>Attendance</span>
          </button>
        )}
      />
      {renderAttendanceModal()}
    </>
  );
};

const StatusBtn = ({ label, title, active, color, onClick }) => {
  return (
    <button
      title={title}
      onClick={onClick}
      style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        border: `1px solid ${active ? color : 'var(--border-color)'}`,
        background: active ? color : 'transparent',
        color: active ? '#fff' : 'var(--text-secondary)',
        fontWeight: 'bold',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.8rem',
        transition: 'all 0.2s'
      }}
    >
      {label}
    </button>
  );
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  backdropFilter: 'blur(4px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 3000,
  padding: 20
};

const modalStyle = {
  background: '#fff',
  padding: 24,
  borderRadius: 16,
  width: '100%',
  maxWidth: 600,
  boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
};

export default LessonsAdmin;
